'use client'

import {
    clearCache,
    getIntegrationCredentials,
    IntegrationCategory,
    IntegrationCredentialItem,
    saveIntegrationCredentials,
    testEmail,
    testMetaCapi,
    revealIntegrationCredential,
} from '@/lib/api/master/master-integrations-api'
import { useEffect, useMemo, useState } from 'react'
import {
    MdBugReport,
    MdCloud,
    MdDeleteSweep,
    MdEmail,
    MdHub,
    MdLocalShipping,
    MdLock,
    MdMemory,
    MdNotifications,
    MdPhone,
    MdReceipt,
    MdSave,
    MdSettings,
    MdSmartToy,
    MdSupportAgent,
    MdVideoCall,
    MdVisibility,
    MdVisibilityOff,
    MdAdsClick,
} from 'react-icons/md'
import { FaGoogle, FaTiktok, FaWhatsapp } from 'react-icons/fa'
import { toast } from 'react-toastify'

import AiUserPromptPanel from './ai-user-prompt-panel'
import WhatsappAccountCard from './whatsapp-account-card'

/** Aba que não é categoria de credencial: o prompt do usuário de IA. */
const AGENT_TAB = 'agent'
type IntegrationTab = IntegrationCategory | typeof AGENT_TAB

const CATEGORY_META: Record<IntegrationCategory, { label: string; icon: any }> =
    {
        google: { label: 'Google (OAuth, Gmail, Calendar, Google Ads)', icon: FaGoogle },
        twilio: { label: 'Twilio (voz / SMS)', icon: MdPhone },
        onesignal: { label: 'OneSignal (push)', icon: MdNotifications },
        daily: { label: 'Daily.co (teleconsulta)', icon: MdVideoCall },
        livekit: { label: 'LiveKit (MediarMeet)', icon: MdVideoCall },
        clicksign: { label: 'Clicksign (assinatura digital)', icon: MdLock },
        focusnfe: { label: 'Focus NFE (fiscal)', icon: MdReceipt },
        email: { label: 'Email (Brevo)', icon: MdEmail },
        openai: { label: 'OpenAI', icon: MdSmartToy },
        cloudflare: { label: 'Cloudflare (R2 + Turnstile)', icon: MdCloud },
        sentry: { label: 'Sentry (monitoramento)', icon: MdBugReport },
        cache: { label: 'Cache', icon: MdMemory },
        app: { label: 'App (feature flags)', icon: MdSettings },
        melhorenvio: {
            label: 'Melhor Envio (OAuth global)',
            icon: MdLocalShipping,
        },
        meta: { label: 'Meta (Facebook/Instagram Ads)', icon: MdAdsClick },
        whatsapp: {
            label: 'WhatsApp (API oficial da Meta)',
            icon: FaWhatsapp,
        },
        tiktok: { label: 'TikTok Ads', icon: FaTiktok },
    }

const CATEGORY_ORDER: IntegrationCategory[] = [
    'google',
    'twilio',
    'onesignal',
    'daily',
    'livekit',
    'clicksign',
    'focusnfe',
    'email',
    'openai',
    'cloudflare',
    'melhorenvio',
    'meta',
    'whatsapp',
    'tiktok',
    'sentry',
    'cache',
    'app',
]

export default function IntegrationsPage() {
    const [items, setItems] = useState<IntegrationCredentialItem[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [activeTab, setActiveTab] = useState<IntegrationTab>('google')
    const activeCategory: IntegrationCategory =
        activeTab === AGENT_TAB ? 'google' : activeTab
    const [drafts, setDrafts] = useState<Record<string, string>>({})
    const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set())
    const [testingCapi, setTestingCapi] = useState(false)
    const [clearingCache, setClearingCache] = useState(false)
    const [testingEmail, setTestingEmail] = useState(false)

    useEffect(() => {
        load()
    }, [])

    const load = async () => {
        setLoading(true)
        try {
            const res = await getIntegrationCredentials()
            if (res.success && res.result) {
                setItems(res.result)
                setDrafts({})
            }
        } finally {
            setLoading(false)
        }
    }

    const itemsByCategory = useMemo(() => {
        const map = new Map<IntegrationCategory, IntegrationCredentialItem[]>()
        for (const cat of CATEGORY_ORDER) map.set(cat, [])
        for (const item of items) map.get(item.category)?.push(item)
        return map
    }, [items])

    const currentItems = itemsByCategory.get(activeCategory) ?? []
    const dirty = currentItems.some(i => drafts[i.key] !== undefined)

    const handleSave = async () => {
        const changedItems = currentItems
            .filter(i => drafts[i.key] !== undefined)
            .map(i => ({ key: i.key, value: drafts[i.key] ?? '' }))

        if (changedItems.length === 0) {
            toast.info('Nenhuma mudança')
            return
        }

        setSaving(true)
        try {
            const res = await saveIntegrationCredentials(changedItems)
            if (res.success) {
                toast.success(
                    `${changedItems.length} credencial(is) salva(s) — override aplicado em runtime`
                )
                load()
            } else {
                toast.error(res.error || 'Erro ao salvar')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleTestCapi = async () => {
        setTestingCapi(true)
        try {
            const res = await testMetaCapi()
            const r = res.result
            if (res.success && r?.ok) {
                toast.success(
                    `CAPI OK (${r.origin}) — pixel ${r.pixelId}, eventos recebidos: ${r.eventsReceived ?? 1}. Confira em Eventos de Teste.`
                )
            } else {
                toast.error(`Falha na CAPI: ${r?.error || res.error || 'erro desconhecido'}`)
            }
        } catch (e: any) {
            toast.error(e?.message || 'Erro ao testar a CAPI')
        } finally {
            setTestingCapi(false)
        }
    }

    const handleClearCache = async () => {
        setClearingCache(true)
        try {
            const res = await clearCache()
            if (res.success) {
                toast.success(
                    `Cache limpo — ${res.result?.cleared ?? 0} chave(s) removida(s) em todas as instâncias`
                )
            } else {
                toast.error(res.error || 'Erro ao limpar o cache')
            }
        } catch (e: any) {
            toast.error(e?.message || 'Erro ao limpar o cache')
        } finally {
            setClearingCache(false)
        }
    }

    const handleTestEmail = async () => {
        setTestingEmail(true)
        try {
            const res = await testEmail()
            const r = res.result
            if (res.success && r?.ok) {
                toast.success(
                    `Email enviado para ${r.sentTo} via ${r.provider} (chave ${r.keyPreview} / ${r.keySource}). Verifique a caixa de entrada.`
                )
            } else {
                toast.error(
                    `Falha (${r?.provider ?? '?'}): ${r?.error || res.error || 'erro desconhecido'} — chave ${r?.keyPreview ?? '?'} via ${r?.keySource ?? '?'}`,
                    { autoClose: 12000 }
                )
            }
        } catch (e: any) {
            toast.error(e?.message || 'Erro ao testar o email')
        } finally {
            setTestingEmail(false)
        }
    }

    /**
     * Alterna a visibilidade do campo. Em credencial exibível (verify token de
     * webhook), busca o valor em claro no servidor — o painel nunca o recebe na
     * listagem, justamente para que segredo não trafegue sem ser pedido.
     */
    const toggleVisible = async (key: string, revealable?: boolean) => {
        const willShow = !visibleKeys.has(key)

        if (willShow && revealable && !drafts[key]) {
            const res = await revealIntegrationCredential(key)
            if (res.error) {
                toast.error(res.error)
                return
            }
            if (res.result?.value) {
                setDrafts(prev => ({ ...prev, [key]: res.result.value }))
            }
        }

        setVisibleKeys(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <MdHub /> Integrações
                </h1>
                <p className="text-base-content/60 text-sm mt-1">
                    Credenciais globais da plataforma (Twilio, Google,
                    OneSignal, etc). Valores criptografados via JWT_SECRET.
                    Override aplicado em runtime sem redeploy.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-4">
                {/* Tabs verticais por categoria */}
                <div className="card bg-base-100 shadow ">
                    <div className="card-body p-2  border-t border-base-300">
                        <ul className="menu menu-vertical w-full">
                            {CATEGORY_ORDER.map(cat => {
                                const meta = CATEGORY_META[cat]
                                const Icon = meta.icon
                                const count =
                                    itemsByCategory.get(cat)?.length ?? 0
                                const configured =
                                    itemsByCategory
                                        .get(cat)
                                        ?.filter(i => i.configured).length ?? 0
                                return (
                                    <li key={cat}>
                                        <a
                                            className={
                                                activeTab === cat
                                                    ? 'active'
                                                    : ''
                                            }
                                            onClick={() => setActiveTab(cat)}
                                        >
                                            <Icon className="text-lg" />
                                            <div className="flex-1">
                                                <div className="text-sm">
                                                    {meta.label}
                                                </div>
                                                <div className="text-xs text-base-content/50">
                                                    {configured}/{count}{' '}
                                                    configurado(s)
                                                </div>
                                            </div>
                                        </a>
                                    </li>
                                )
                            })}
                            <li>
                                <a
                                    className={
                                        activeTab === AGENT_TAB ? 'active' : ''
                                    }
                                    onClick={() => setActiveTab(AGENT_TAB)}
                                >
                                    <MdSupportAgent className="text-lg" />
                                    <div className="flex-1">
                                        <div className="text-sm">Agente</div>
                                        <div className="text-xs text-base-content/50">
                                            Prompt do usuário de IA
                                        </div>
                                    </div>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Painel da categoria selecionada */}
                <div className="card bg-base-100 shadow border-t border-base-300 min-w-0">
                    {activeTab === AGENT_TAB ? (
                        <div className="card-body">
                            <h2 className="card-title mb-2">
                                Agente: prompt do usuário de IA
                            </h2>
                            <AiUserPromptPanel />
                        </div>
                    ) : (
                    <div className="card-body">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="card-title">
                                {CATEGORY_META[activeCategory].label}
                            </h2>
                            <div className="flex items-center gap-2">
                                {activeCategory === 'email' && (
                                    <button
                                        className="btn btn-sm btn-outline gap-1"
                                        onClick={handleTestEmail}
                                        disabled={testingEmail}
                                        title="Envia um email de teste para o seu próprio email com as credenciais atuais"
                                    >
                                        {testingEmail ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdEmail />
                                        )}
                                        Testar email
                                    </button>
                                )}
                                {activeCategory === 'cache' && (
                                    <button
                                        className="btn btn-sm btn-outline btn-error gap-1"
                                        onClick={handleClearCache}
                                        disabled={clearingCache}
                                        title="Remove todas as chaves de cache (Redis) sem afetar as filas. Limpa todas as instâncias."
                                    >
                                        {clearingCache ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdDeleteSweep />
                                        )}
                                        Limpar cache
                                    </button>
                                )}
                                {activeCategory === 'meta' && (
                                    <button
                                        className="btn btn-sm btn-outline gap-1"
                                        onClick={handleTestCapi}
                                        disabled={testingCapi}
                                        title="Dispara um Purchase de teste pela CAPI usando o Pixel e Token de Plataforma"
                                    >
                                        {testingCapi ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdBugReport />
                                        )}
                                        Testar CAPI
                                    </button>
                                )}
                                {dirty && (
                                    <span className="badge badge-warning">
                                        Não salvo
                                    </span>
                                )}
                            </div>
                        </div>

                        {loading && (
                            <div className="flex items-center justify-center min-h-[200px]">
                                <div className="loading loading-spinner loading-lg text-secondary"></div>
                            </div>
                        )}

                        {!loading && currentItems.length === 0 && (
                            <div className="text-center py-12 text-base-content/60">
                                Nenhuma credencial nesta categoria.
                            </div>
                        )}

                        {!loading && currentItems.length > 0 && (
                            <div className="space-y-4">
                                {currentItems.map(item => {
                                    const isVisible = visibleKeys.has(item.key)
                                    const draftValue = drafts[item.key]
                                    const inputValue =
                                        draftValue !== undefined
                                            ? draftValue
                                            : item.isSecret
                                              ? ''
                                              : (item.value ?? '')
                                    const boolChecked =
                                        (draftValue ??
                                            item.value ??
                                            'false') === 'true'

                                    return (
                                        <div
                                            key={item.key}
                                            className="form-control"
                                        >
                                            <div className="flex items-baseline justify-between gap-4 mb-1">
                                                <label className="label-text font-medium flex items-center gap-2">
                                                    {item.label}
                                                    {item.configured && (
                                                        <span className="badge badge-xs badge-success">
                                                            configurado
                                                        </span>
                                                    )}
                                                </label>
                                                {item.description && (
                                                    <span className="label-text-alt text-base-content/50 text-xs">
                                                        {item.description}
                                                    </span>
                                                )}
                                            </div>

                                            {item.type === 'bool' ? (
                                                <label className="label cursor-pointer justify-start gap-3">
                                                    <input
                                                        type="checkbox"
                                                        className="toggle toggle-secondary"
                                                        checked={boolChecked}
                                                        onChange={e =>
                                                            setDrafts(prev => ({
                                                                ...prev,
                                                                [item.key]: e
                                                                    .target
                                                                    .checked
                                                                    ? 'true'
                                                                    : 'false',
                                                            }))
                                                        }
                                                    />
                                                    <span className="label-text text-sm">
                                                        {boolChecked
                                                            ? 'Ativado'
                                                            : 'Desativado'}
                                                    </span>
                                                </label>
                                            ) : item.isSecret ? (
                                                <div className="join w-full">
                                                    <input
                                                        type={
                                                            isVisible
                                                                ? 'text'
                                                                : 'password'
                                                        }
                                                        className="input input-bordered join-item flex-1 font-mono text-sm"
                                                        placeholder={
                                                            item.configured
                                                                ? '••••••••••  (em branco mantém)'
                                                                : (item.placeholder ??
                                                                  '')
                                                        }
                                                        value={inputValue}
                                                        onChange={e =>
                                                            setDrafts(prev => ({
                                                                ...prev,
                                                                [item.key]:
                                                                    e.target
                                                                        .value,
                                                            }))
                                                        }
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-square join-item"
                                                        onClick={() =>
                                                            void toggleVisible(
                                                                item.key,
                                                                item.revealable
                                                            )
                                                        }
                                                        title={
                                                            isVisible
                                                                ? 'Ocultar'
                                                                : item.revealable
                                                                  ? 'Mostrar o valor salvo'
                                                                  : 'Mostrar o que foi digitado (o valor salvo não é exibível)'
                                                        }
                                                    >
                                                        {isVisible ? (
                                                            <MdVisibilityOff />
                                                        ) : (
                                                            <MdVisibility />
                                                        )}
                                                    </button>
                                                </div>
                                            ) : (
                                                <input
                                                    type={
                                                        item.type === 'number'
                                                            ? 'number'
                                                            : 'text'
                                                    }
                                                    className="input input-bordered font-mono text-sm"
                                                    placeholder={
                                                        item.placeholder ?? ''
                                                    }
                                                    value={inputValue}
                                                    onChange={e =>
                                                        setDrafts(prev => ({
                                                            ...prev,
                                                            [item.key]:
                                                                e.target.value,
                                                        }))
                                                    }
                                                />
                                            )}
                                            {item.updatedAt && (
                                                <span className="text-xs text-base-content/40 mt-1">
                                                    atualizado{' '}
                                                    {new Date(
                                                        item.updatedAt
                                                    ).toLocaleString('pt-BR', {
                                                        timeZone:
                                                            'America/Sao_Paulo',
                                                    })}
                                                </span>
                                            )}
                                        </div>
                                    )
                                })}

                                <div className="card-actions justify-end pt-4 border-t border-base-300">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={handleSave}
                                        disabled={saving || !dirty}
                                    >
                                        {saving ? (
                                            <span className="loading loading-spinner loading-sm" />
                                        ) : (
                                            <MdSave />
                                        )}
                                        Salvar credenciais desta categoria
                                    </button>
                                </div>
                            </div>
                        )}

                        {!loading && activeCategory === 'whatsapp' && (
                            <WhatsappAccountCard />
                        )}
                    </div>
                    )}
                </div>
            </div>
        </div>
    )
}
